import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { normalizeCompareExploreColumns } from '../performance/compareExplore/exploreColumns';
import { resolveThemeId } from '../styles/themeTokens';
import {
  PREFERRED_THEME_STORAGE_KEY,
  persistAppearancePreferences,
  resolveStoredThemePreference,
} from '../utils/appearancePrefs';

// Defaults kept in-hook so App.jsx (and any test harness) doesn't have to
// re-derive them. Values match the pre-hook constants that used to live in
// App.jsx directly.
const DEFAULT_IDLE_LOGOUT_MINUTES = 30;
const DEFAULT_NEWS_RECENCY_HOURS = 24;
// Mirror `NEWS_RECENCY_HOURS_OPTIONS` in
// `functions/services/appearance_preferences_service.py`, which rejects the
// whole preferences save for any other value.
const ALLOWED_NEWS_RECENCY_HOURS = new Set([1, 2, 6, 24, 48]);
const COMPACT_MODE_STORAGE_KEY = 'compactMode';
const COLOR_OVERRIDES_STORAGE_KEY = 'colorOverrides';
const SECTOR_PALETTE_STORAGE_KEY = 'sectorPalette';
export const APPEARANCE_PERSIST_DEBOUNCE_MS = 600;

// Mirror `_APPROVED_FOCUS_AREAS` in `functions/services/deep_dive_service.py`.
// The backend re-validates Deep Dive requests and saved preferences against
// it, rejecting the whole preferences save for any other value.
const DEFAULT_DEEP_DIVE_FOCUS_AREAS = Object.freeze(['all']);
export const ALLOWED_DEEP_DIVE_FOCUS_AREAS = Object.freeze([
  'all',
  'price_action',
  'company_news',
  'earnings_fundamentals',
  'macro_sector',
  'regulatory_legal',
  'risks_catalysts',
]);
const ALLOWED_DEEP_DIVE_FOCUS_AREAS_SET = new Set(ALLOWED_DEEP_DIVE_FOCUS_AREAS);

const normalizeNewsRecencyHours = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_NEWS_RECENCY_HOURS;
  const rounded = Math.round(numeric);
  return ALLOWED_NEWS_RECENCY_HOURS.has(rounded)
    ? rounded
    : DEFAULT_NEWS_RECENCY_HOURS;
};

// Falls back to ['all'] on any invalid input, matching the backend
// contract for a missing / empty payload. Enforces the `all`-vs-specifics
// mutex expected by the UI.
const normalizeDeepDiveFocusAreas = (value) => {
  if (!Array.isArray(value)) return [...DEFAULT_DEEP_DIVE_FOCUS_AREAS];
  const cleaned = [];
  const seen = new Set();
  for (const raw of value) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim().toLowerCase();
    if (!trimmed || !ALLOWED_DEEP_DIVE_FOCUS_AREAS_SET.has(trimmed)) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    cleaned.push(trimmed);
  }
  if (cleaned.length === 0) return [...DEFAULT_DEEP_DIVE_FOCUS_AREAS];
  if (cleaned.includes('all')) return ['all'];
  return cleaned;
};

// Mirrors the backend rule for `maxSharpeSkill` in
// `functions/services/appearance_preferences_service.py`: above 0 and at most 1.
// Anything else (a cleared or half-typed input) is not remembered.
const normalizeMaxSharpeSkill = (value) => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 1 ? value : null
);

const isPlainObject = (value) => (
  value != null && typeof value === 'object' && !Array.isArray(value)
);

// Stable stringification with recursively sorted keys so two payloads with
// the same content but different key-insertion order still compare equal.
// Recursion matters for the nested `colorOverrides` and `sectorPalette`
// blobs, whose key order after a JSON.parse round-trip is not guaranteed to
// match what the user built up locally via successive setColorOverride() /
// setSectorColor() calls.
const stableStringify = (value) => {
  if (value === null || value === undefined) {
    return JSON.stringify(value ?? null);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (!isPlainObject(value)) {
    return JSON.stringify(value);
  }
  const sortedKeys = Object.keys(value).sort();
  const parts = sortedKeys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`);
  return `{${parts.join(',')}}`;
};

// Freeze the whole appearance-prefs slice into one stable string so the
// persistence effect can compare "current" against "last successfully loaded
// or saved" with a single === and no deep-walk noise.
const buildAppearancePrefsSnapshot = ({
  themeId,
  compactMode,
  colorOverrides,
  idleLogoutMinutes,
  newsRecencyHours,
  sectorPalette,
  deepDiveFocusAreas,
  maxSharpeSkill,
  compareExploreColumns,
}) => stableStringify({
  themeId: themeId ?? null,
  compactMode: Boolean(compactMode),
  colorOverrides: isPlainObject(colorOverrides) ? colorOverrides : {},
  idleLogoutMinutes: idleLogoutMinutes === undefined ? null : idleLogoutMinutes,
  newsRecencyHours: newsRecencyHours ?? null,
  sectorPalette: isPlainObject(sectorPalette) ? sectorPalette : {},
  deepDiveFocusAreas: Array.isArray(deepDiveFocusAreas)
    ? deepDiveFocusAreas
    : [...DEFAULT_DEEP_DIVE_FOCUS_AREAS],
  maxSharpeSkill: maxSharpeSkill ?? null,
  compareExploreColumns: compareExploreColumns ?? null,
});

const readCompactModeFromStorage = () => {
  try {
    const saved = window.localStorage.getItem(COMPACT_MODE_STORAGE_KEY);
    return saved ? JSON.parse(saved) : false;
  } catch (_) {
    return false;
  }
};

const readColorOverridesFromStorage = () => {
  try {
    const saved = window.localStorage.getItem(COLOR_OVERRIDES_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
};

const readSectorPaletteFromStorage = () => {
  if (typeof window === 'undefined') return {};
  try {
    const saved = window.localStorage.getItem(SECTOR_PALETTE_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
};

/**
 * Single owner for the appearance-preferences slice.
 *
 * Owns all appearance state (theme, compact mode, color overrides, idle logout
 * minutes, news recency hours, sector palette override), seeds it from
 * localStorage for the pre-auth cache path, hydrates from
 * `userData.appearance_preferences` when the server payload arrives, and runs
 * a single debounced POST that persists the combined slice only when the
 * normalized value drifts from the last-successfully-loaded-or-saved snapshot.
 *
 * The persistence effect is guarded by a *value comparison*, not a "hydrated"
 * boolean phase flag — the boolean approach used to flip true synchronously in
 * the hydration effect and then let the very next persistence-effect tick
 * echo the just-hydrated values back to the server. Snapshot comparison is
 * race-free: whatever the state settles to after hydration equals what we
 * stamped into the snapshot at the end of the hydration effect, so the
 * persistence effect's diff check returns "no change" and no write is
 * scheduled.
 *
 * Cancellation happens on logout (`userData` transitions to null: snapshot
 * resets, pending timer cleared) and on unmount (persistence effect cleanup
 * clears the timer). An in-flight fetch that already left the client cannot
 * be recalled, but no *new* writes can start after those transitions.
 */
export function useAppearancePreferencesSync({
  userData,
  persist = persistAppearancePreferences,
} = {}) {
  const [themeId, setThemeId] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = window.localStorage.getItem(PREFERRED_THEME_STORAGE_KEY);
    return resolveStoredThemePreference(saved);
  });
  const [compactMode, setCompactMode] = useState(() => (
    typeof window === 'undefined' ? false : readCompactModeFromStorage()
  ));
  const toggleCompactMode = useCallback(() => setCompactMode((prev) => !prev), []);
  const [colorOverrides, setColorOverrides] = useState(() => (
    typeof window === 'undefined' ? {} : readColorOverridesFromStorage()
  ));
  const setColorOverride = useCallback((token, hex) => (
    setColorOverrides((prev) => ({ ...prev, [token]: hex }))
  ), []);
  const resetColorOverride = useCallback((token) => (
    setColorOverrides((prev) => {
      if (!(token in prev)) return prev;
      const next = { ...prev };
      delete next[token];
      return next;
    })
  ), []);
  const resetAllColorOverrides = useCallback(() => setColorOverrides({}), []);
  const [idleLogoutMinutes, setIdleLogoutMinutes] = useState(DEFAULT_IDLE_LOGOUT_MINUTES);
  const [newsRecencyHoursState, setNewsRecencyHoursState] = useState(DEFAULT_NEWS_RECENCY_HOURS);
  const setNewsRecencyHours = useCallback((value) => {
    setNewsRecencyHoursState(normalizeNewsRecencyHours(value));
  }, []);
  const [sectorPaletteOverride, setSectorPaletteOverride] = useState(readSectorPaletteFromStorage);
  // Server-authoritative after hydration. No localStorage cache — Deep Dive
  // is auth-gated so the pre-auth-cache pattern used for theme / compact
  // mode / color overrides doesn't apply.
  const [deepDiveFocusAreasState, setDeepDiveFocusAreasState] = useState(
    () => [...DEFAULT_DEEP_DIVE_FOCUS_AREAS],
  );
  const setDeepDiveFocusAreas = useCallback((value) => {
    setDeepDiveFocusAreasState(normalizeDeepDiveFocusAreas(value));
  }, []);
  // The skill a member last entered for Max Sharpe; null until they enter one.
  // Server-authoritative like the Deep Dive focus areas.
  const [maxSharpeSkillState, setMaxSharpeSkillState] = useState(null);
  const setMaxSharpeSkill = useCallback((value) => {
    const skill = normalizeMaxSharpeSkill(value);
    if (skill !== null) setMaxSharpeSkillState(skill);
  }, []);
  // The member's Compare & Explore grid columns; null until they arrange
  // them. Server-authoritative like the Max Sharpe skill.
  const [compareExploreColumnsState, setCompareExploreColumnsState] = useState(null);
  const setCompareExploreColumns = useCallback((value) => {
    const columns = normalizeCompareExploreColumns(value);
    if (columns !== null) setCompareExploreColumnsState(columns);
  }, []);

  // localStorage sync mirrors the pre-auth cache contract Trevor called out:
  // "Keep server preferences authoritative after authentication while
  // retaining localStorage as the pre-auth/local fallback."
  useEffect(() => {
    try {
      window.localStorage.setItem(PREFERRED_THEME_STORAGE_KEY, JSON.stringify(themeId));
    } catch (_) { /* ignore */ }
  }, [themeId]);
  useEffect(() => {
    try {
      window.localStorage.setItem(COMPACT_MODE_STORAGE_KEY, JSON.stringify(compactMode));
    } catch (_) { /* ignore */ }
  }, [compactMode]);
  useEffect(() => {
    try {
      window.localStorage.setItem(COLOR_OVERRIDES_STORAGE_KEY, JSON.stringify(colorOverrides));
    } catch (_) { /* ignore */ }
  }, [colorOverrides]);
  useEffect(() => {
    try {
      window.localStorage.setItem(SECTOR_PALETTE_STORAGE_KEY, JSON.stringify(sectorPaletteOverride));
    } catch (_) { /* ignore */ }
  }, [sectorPaletteOverride]);

  const lastPersistedSnapshotRef = useRef(null);
  const persistTimerRef = useRef(null);
  const hydratedServerPrefsRef = useRef(null);

  // Hydration effect — runs when server payload arrives (or clears on logout).
  // It hydrates only when the server's preferences differ from the ones it
  // last hydrated, not on every new `userData` object.
  useEffect(() => {
    if (!userData) {
      lastPersistedSnapshotRef.current = null;
      hydratedServerPrefsRef.current = null;
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      return;
    }
    const prefs = userData.appearance_preferences || {};
    // Other userData updates (a Watchlist publication refreshes a few
    // fields) still carry the preferences read at sign-in. Re-applying them
    // would undo changes the user has made since.
    const serverPrefsKey = JSON.stringify(prefs);
    if (serverPrefsKey === hydratedServerPrefsRef.current) {
      return;
    }
    hydratedServerPrefsRef.current = serverPrefsKey;

    // Compute the "post-hydration" state so the snapshot we stamp equals what
    // the persistence effect will see on the next tick, race-free.
    const hydratedThemeId = prefs.themeId ? resolveThemeId(prefs.themeId) : themeId;
    const hydratedCompactMode = typeof prefs.compactMode === 'boolean' ? prefs.compactMode : compactMode;
    const hydratedColorOverrides = (prefs.colorOverrides && typeof prefs.colorOverrides === 'object')
      ? prefs.colorOverrides
      : colorOverrides;
    const hydratedIdleLogoutMinutes = 'idleLogoutMinutes' in prefs
      ? prefs.idleLogoutMinutes
      : idleLogoutMinutes;
    const hydratedNewsRecencyHours = 'newsRecencyHours' in prefs
      ? normalizeNewsRecencyHours(prefs.newsRecencyHours)
      : newsRecencyHoursState;
    const hydratedSectorPalette = (prefs.sectorPalette && typeof prefs.sectorPalette === 'object')
      ? prefs.sectorPalette
      : sectorPaletteOverride;
    const hydratedDeepDiveFocusAreas = 'deepDiveFocusAreas' in prefs
      ? normalizeDeepDiveFocusAreas(prefs.deepDiveFocusAreas)
      : deepDiveFocusAreasState;
    // A member who never entered a skill starts from the default, never from
    // the previous member's.
    const hydratedMaxSharpeSkill = normalizeMaxSharpeSkill(prefs.maxSharpeSkill);
    // Likewise a member who never arranged the grid starts from its default.
    const hydratedCompareExploreColumns = normalizeCompareExploreColumns(prefs.compareExploreColumns);

    if (prefs.themeId) setThemeId(hydratedThemeId);
    if (typeof prefs.compactMode === 'boolean') setCompactMode(hydratedCompactMode);
    if (prefs.colorOverrides && typeof prefs.colorOverrides === 'object') {
      setColorOverrides(hydratedColorOverrides);
    }
    // `in` (not `is not None`) so a stored null ("Never") is honored rather
    // than reverting to the default.
    if ('idleLogoutMinutes' in prefs) setIdleLogoutMinutes(hydratedIdleLogoutMinutes);
    if ('newsRecencyHours' in prefs) setNewsRecencyHoursState(hydratedNewsRecencyHours);
    if (prefs.sectorPalette && typeof prefs.sectorPalette === 'object') {
      setSectorPaletteOverride(hydratedSectorPalette);
    }
    if ('deepDiveFocusAreas' in prefs) setDeepDiveFocusAreasState(hydratedDeepDiveFocusAreas);
    setMaxSharpeSkillState(hydratedMaxSharpeSkill);
    setCompareExploreColumnsState(hydratedCompareExploreColumns);

    lastPersistedSnapshotRef.current = buildAppearancePrefsSnapshot({
      themeId: hydratedThemeId,
      compactMode: hydratedCompactMode,
      colorOverrides: hydratedColorOverrides,
      idleLogoutMinutes: hydratedIdleLogoutMinutes,
      newsRecencyHours: hydratedNewsRecencyHours,
      sectorPalette: hydratedSectorPalette,
      deepDiveFocusAreas: hydratedDeepDiveFocusAreas,
      maxSharpeSkill: hydratedMaxSharpeSkill,
      compareExploreColumns: hydratedCompareExploreColumns,
    });
    // Stale-closure on the sibling state values is intentional — we only use
    // them as defaults when the server didn't send that key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Persistence effect — value comparison, not a boolean phase flag.
  useEffect(() => {
    const baselineSnapshot = lastPersistedSnapshotRef.current;
    if (baselineSnapshot === null) {
      return undefined;
    }
    const currentSnapshot = buildAppearancePrefsSnapshot({
      themeId,
      compactMode,
      colorOverrides,
      idleLogoutMinutes,
      newsRecencyHours: newsRecencyHoursState,
      sectorPalette: sectorPaletteOverride,
      deepDiveFocusAreas: deepDiveFocusAreasState,
      maxSharpeSkill: maxSharpeSkillState,
      compareExploreColumns: compareExploreColumnsState,
    });
    if (currentSnapshot === baselineSnapshot) {
      return undefined;
    }
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
    }
    // Capture the exact payload + snapshot so the commit-after-success step
    // reflects what the server just accepted, not whatever state has drifted
    // to during the debounce window.
    const payload = {
      themeId,
      compactMode,
      colorOverrides,
      idleLogoutMinutes,
      newsRecencyHours: newsRecencyHoursState,
      sectorPalette: sectorPaletteOverride,
      deepDiveFocusAreas: deepDiveFocusAreasState,
      // The backend accepts only a real skill, so an unset one is left out.
      ...(maxSharpeSkillState !== null ? { maxSharpeSkill: maxSharpeSkillState } : {}),
      ...(compareExploreColumnsState !== null ? { compareExploreColumns: compareExploreColumnsState } : {}),
    };
    const payloadSnapshot = currentSnapshot;
    persistTimerRef.current = setTimeout(async () => {
      persistTimerRef.current = null;
      const persisted = await persist(payload);
      if (persisted !== null) {
        lastPersistedSnapshotRef.current = payloadSnapshot;
      }
    }, APPEARANCE_PERSIST_DEBOUNCE_MS);
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
    };
  }, [
    themeId,
    compactMode,
    colorOverrides,
    idleLogoutMinutes,
    newsRecencyHoursState,
    sectorPaletteOverride,
    deepDiveFocusAreasState,
    maxSharpeSkillState,
    compareExploreColumnsState,
    persist,
  ]);

  // Convenience return: expose everything App.jsx needs plus the derived
  // read-only alias `newsRecencyHours` to keep the existing prop drilling
  // shape (`{newsRecencyHours}`) unchanged for downstream consumers.
  return useMemo(() => ({
    themeId, setThemeId,
    compactMode, setCompactMode, toggleCompactMode,
    colorOverrides, setColorOverride, resetColorOverride, resetAllColorOverrides,
    idleLogoutMinutes, setIdleLogoutMinutes,
    newsRecencyHours: newsRecencyHoursState, setNewsRecencyHours,
    sectorPaletteOverride, setSectorPaletteOverride,
    deepDiveFocusAreas: deepDiveFocusAreasState, setDeepDiveFocusAreas,
    maxSharpeSkill: maxSharpeSkillState, setMaxSharpeSkill,
    compareExploreColumns: compareExploreColumnsState, setCompareExploreColumns,
  }), [
    themeId,
    compactMode, toggleCompactMode,
    colorOverrides, setColorOverride, resetColorOverride, resetAllColorOverrides,
    idleLogoutMinutes,
    newsRecencyHoursState, setNewsRecencyHours,
    sectorPaletteOverride,
    deepDiveFocusAreasState, setDeepDiveFocusAreas,
    maxSharpeSkillState, setMaxSharpeSkill,
    compareExploreColumnsState, setCompareExploreColumns,
  ]);
}
